import axios from 'axios';
import { apiService } from '@signals/app-shell/services/api';
import { debugLog } from '@signals/app-shell/utils/debugLog';
import { wallClockDateInTimeZoneToUtcIso } from '@signals/app-shell/utils/time';

// Use the same API base URL configuration as the main API service
const getApiBaseUrl = () => {
  return process.env.REACT_APP_API_URL || '/api';
};

const API_BASE_URL = getApiBaseUrl();

// Types for Pathfinder API
export interface PathfinderDevice {
  id: string;
  type: 'watch' | 'vehicle' | 'phone';
  name?: string;
  active: boolean;
  color?: string;
  lastSeen?: Date;
  batteryLevel?: number;
  coordinates?: [number, number];
}

export interface PathfinderStudent {
  id: string;
  name: string;
  devices: PathfinderDevice[];
  course: string;
  status: 'active' | 'inactive';
}

export interface PathfinderDataPoint {
  id: string;
  deviceId: string;
  deviceType?: 'watch' | 'vehicle' | 'phone';
  studentId: string;
  timestamp: Date;
  coordinates: [number, number];
  accuracy: number;
  speed?: number; // Speed in km/h from device GPS
  bearing?: number; // Heading/direction in degrees
  activity?: string;
  metadata?: Record<string, any>;
  /** When set, point was loaded from a specific area-search circle (for per-tab map scoping). */
  areaSearchCircleId?: string;
}

export interface PathfinderRoute {
  id: string;
  studentId: string;
  deviceId: string;
  deviceType: 'watch' | 'vehicle' | 'phone';
  points: PathfinderDataPoint[];
  coordinates: [number, number][]; // Array of [lat, lng] for line drawing
  startTime: Date;
  endTime: Date;
  totalDistance: number; // in meters
  color: string; // Color for visualization
}

export interface PathfinderQuery {
  dateRange: [Date, Date];
  timeZone?: string;
  studentIds?: string[];
  deviceTypes?: string[];
  selectedCourse?: string; // Course ID for SENTINEL context
  selectedDatabase?: string; // Database tag for filtering
  spatialFilter?: {
    center: [number, number];
    radius: number; // in meters
  };
  timeFilter?: {
    startTime: string; // HH:MM format
    endTime: string;   // HH:MM format
  };
  limit?: number;
}

export interface TimeWindowQuery {
  fullDateRange: [Date, Date];
  windowDateRange: [Date, Date];
  timeZone?: string;
  studentIds: string[];
  deviceTypes?: string[];
  selectedCourse?: string;
  selectedDatabase?: string;
}

export interface CoTravelerEncounter {
  timestamp: Date;
  location: [number, number];
  duration: number; // in minutes
  proximity: number; // in meters
  confidence: number; // 0-1 confidence score
  activity?: string; // dwell, transit, etc.
  locationName?: string; // human-readable location
}

export interface CoTravelerResult {
  primaryDevice: string;
  coTravelerDevice: string;
  // 'Student' | 'SRP' | 'RP' | 'Unknown' for track-based co-travelers;
  // 'WIFI' | 'BLUETOOTH' | 'CELLULAR' for digital (RF-emitter) co-travelers;
  // 'ADTECH' for Venntel/GeoNeutron commercial mobile-location co-travelers.
  coTravelerEntityType: 'Student' | 'SRP' | 'RP' | 'Unknown' | 'WIFI' | 'BLUETOOTH' | 'CELLULAR' | 'ADTECH';
  encounters: CoTravelerEncounter[];
  totalEncounters: number;
  totalDuration: number;
  averageProximity: number;
  confidenceScore: number;
  temporalOverlap: number; // percentage of time overlap
  spatialOverlap: number; // percentage of spatial overlap
  frequentLocations: Array<{
    location: [number, number];
    name?: string;
    encounters: number;
    totalDuration: number;
  }>;
}

export interface CoTravelerAnalysisParams {
  deviceIds: string[];
  dateRange: [Date, Date];
  timeZone?: string;
  proximityThreshold: number; // meters
  timeThreshold: number; // minutes
  courseId?: string; // Course ID for co-traveler search (undefined = search all courses for RP/SRP)
  primaryCourseId?: string; // Course ID for primary student data (ALWAYS the selected course)
  databaseTag?: string; // Database tag/name to keep queries in correct database
  entityTypesFilter?: string[]; // Filter co-travelers by entity type: ['Student'], ['RP', 'SRP'], etc.
}

// Cross-area request/response types (SET.NET 8 style)
interface CrossArea {
  latitude: number;
  longitude: number;
  radius: number; // meters
  start_date: string; // ISO
  end_date: string;   // ISO
}

interface CoTravelerItemDTO {
  id: string;
  count: number;
}

interface CoTravelerMatchDTO {
  entity_id?: string;
  device_id?: string;
  entity_type?: string;  // Student, SRP, RP, etc.
  timestamp: string;
  coordinates: [number, number];
  area_index: number;
  distance_m: number;
}

interface CoTravelerResponseDTO {
  items: CoTravelerItemDTO[];
  matches: CoTravelerMatchDTO[];
  // Debug info from backend (optional, for troubleshooting)
  debug_sql_candidates?: number;
  debug_time_range?: string;
  debug_bbox?: string;
  debug_entity_types_filter?: string[];
}

interface CoTravelerRequest {
  course_id?: string;
  database_tag?: string;
  primary_ids?: string[];
  cross_area_radius?: number;
  areas: CrossArea[];
  entity_types_filter?: string[]; // Filter co-travelers by entity type at DB level
}

export interface PatternOfLifeResult {
  deviceId: string;
  patterns: {
    digital: {
      callActivity: Array<{ hour: number; count: number }>;
      smsActivity: Array<{ hour: number; count: number }>;
      webActivity: Array<{ hour: number; count: number }>;
    };
    physical: {
      dwellLocations: Array<{
        location: [number, number];
        address?: string;
        visits: number;
        totalDuration: number; // in minutes
        averageDuration: number;
      }>;
      movementPatterns: Array<{
        fromLocation: [number, number];
        toLocation: [number, number];
        frequency: number;
        averageTime: number; // in minutes
      }>;
    };
  };
}

export interface SignalEnvironmentData {
  wifi: any[];
  bluetooth: any[];
  cellular: any[];
  gnss: any;
  summary: {
    device_ids: string[];
    time_range: string;
    counts: Record<string, number>;
    total_unique_wifi: number;
    total_unique_bluetooth: number;
    total_unique_cellular: number;
  };
  query_time_ms: number;
}

export interface CompareSignalEntry {
  areas_found: number;
  area_ids: string[];
  observation_count: number;
  first_seen: string | null;
  last_seen: string | null;
  [key: string]: any;
}

export interface SignalCompareResult {
  wifi: CompareSignalEntry[];
  bluetooth: CompareSignalEntry[];
  cellular: CompareSignalEntry[];
  area_count: number;
  mode: string;
  summary: Record<string, any>;
  query_time_ms: number;
}

// AdTech (Venntel / GeoNeutron) module -----------------------------------
export interface AdtechStatus {
  /** Master ops switch (ADTECH_ENABLED env). */
  enabled: boolean;
  /** GeoNeutron credentials present. */
  configured: boolean;
  /** Settings -> Modules "AdTech" toggle. */
  module_enabled: boolean;
  /** enabled && configured && module_enabled — the value the UI gates on. */
  available: boolean;
  max_areas: number;
  max_date_range_days: number;
}

export interface AdtechCandidate {
  external_id: string; // GeoNeutron registrationID
  signal_type: string; // 'adtech'
  display_name?: string;
  sighting_count: number;
  distinct_locations: number;
  first_seen: string;
  last_seen: string;
  time_span_minutes: number;
  co_occurrence_score: number;
  confidence: number;
  locations: Array<{ latitude: number; longitude: number; recorded_at: string }>;
  carrier?: string | null;
  country?: string | null;
  forensic_flags: string[];
}

export interface AdtechCoTravelerResult {
  candidates: AdtechCandidate[];
  operator_points: number;
  search_areas: number;
  total_signals_scanned: number;
  query_time_ms: number;
}

export interface AdtechAreaSearchResult {
  devices: Array<Record<string, any>>;
  unique_count: number;
  total_signals: number;
  query_time_ms: number;
}

export interface AdtechPatternOfLifeResult {
  // Signals grouped by registration id, each list sorted by timestamp.
  devices: Record<string, Array<Record<string, any>>>;
  total_devices: number;
  total_signals: number;
  query_time_ms: number;
}

export interface AdtechDiscoverResult {
  unique_devices: number;
  total_signals: number;
  estimated: boolean;
  query_time_ms: number;
}

export interface AdtechDemographicsResult {
  // Raw per-device demographic dicts (Venntel BS Amplify) keyed loosely; and a
  // {registrationID: hashedEmail} map. Either may be empty if unlicensed.
  demographics: Array<Record<string, any>>;
  hashed_emails: Record<string, string>;
  query_time_ms: number;
}

// ---- Overwatch (GDELT media via DataHog) ----
export interface OverwatchStatus {
  module_enabled: boolean;
  sandbox_configured: boolean;
  available: boolean;
}

export interface OverwatchLocation {
  latitude: number;
  longitude: number;
  name?: string;
  country?: string;
}

export interface OverwatchArticle {
  id: string;
  title: string;
  headline?: string;
  url?: string;
  language?: string;         // "en" | "non-en"
  is_translated?: boolean;
  published_at?: string;
  primary_category?: string;
  sentiment?: string;
  sentiment_score?: number;
  latitude?: number;
  longitude?: number;
  location_name?: string;
  country?: string;
  // Every geocoded place mentioned in the article; first entry mirrors
  // latitude/longitude. Used to plot all points and zoom-to-extent.
  locations?: OverwatchLocation[];
  source?: string;
  summary?: string;
  // Full untruncated body — only present on the detail (getOverwatchArticle) fetch.
  full_text?: string;
  themes?: string[];
  persons?: string[];
  organizations?: string[];
}

export interface OverwatchSearchResult {
  articles: OverwatchArticle[];
  total_count: number;
  search_type: string;
  filters_applied: Record<string, any>;
  query_time_ms: number;
}

export interface OverwatchSearchParams {
  mode?: 'keyword' | 'area' | 'radius';
  query?: string;
  language?: 'en' | 'foreign' | 'all';
  category?: string;
  days?: number;
  dateRange?: [Date, Date];
  timeZone?: string;
  limit?: number;
  // area bbox
  minLat?: number;
  maxLat?: number;
  minLon?: number;
  maxLon?: number;
  // radius
  centerLat?: number;
  centerLon?: number;
  radiusKm?: number;
}

// ---- Saved Devices (watchlist) ----
export type SavedDeviceKind = 'student' | 'adtech';

export interface SavedDevice {
  id: number;
  registration_id: string;
  name?: string;
  notes?: string;
  tags?: string[];
  created_by?: string;
  created_at: string;
  updated_at: string;
  course_id?: string;
  active: boolean;
  device_kind?: SavedDeviceKind | string;
  device_type?: string;
}

export interface SaveDeviceParams {
  registrationId: string;
  name?: string;
  notes?: string;
  tags?: string[];
  courseId?: string;
  deviceKind?: SavedDeviceKind;
  deviceType?: string;
}

// ---- Saved Areas (geofences) ----
// Backed by the existing /api/geofences store. A saved area persists a drawn
// circle as a polygon (so it also works with generic geofence tooling) while
// stashing the original {center, radius} in `description` as JSON so it can be
// reloaded as an editable circle in Area Search.
export interface SavedAreaGeometry {
  type: string; // 'Polygon'
  coordinates: number[][][]; // [[[lng, lat], ...]]
}

export interface SavedArea {
  id: number;
  name: string;
  description?: string | null;
  geometry: SavedAreaGeometry;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
  course_id?: string | null;
  active: boolean;
}

export interface SaveAreaParams {
  name: string;
  /** Circle center as [lat, lon]. */
  center: [number, number];
  /** Circle radius in meters. */
  radiusMeters: number;
  courseId?: string;
  /** Optional human place label to store alongside the circle metadata. */
  placeName?: string;
}

export class PathfinderApiService {
  private client = axios.create({
    baseURL: API_BASE_URL,
    timeout: 120000, // Increased to 2 minutes for large datasets
  });

  constructor() {
    // Add authentication interceptor
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  private serializeQueryDate(date: Date, timeZone?: string): string {
    return timeZone ? wallClockDateInTimeZoneToUtcIso(date, timeZone) : date.toISOString();
  }

  // Method: Query timeExtent data (replicates 's server-side filtering)
  async queryTimeExtent(query: TimeWindowQuery): Promise<PathfinderDataPoint[]> {
    try {
      // TODO: Implement PostgreSQL version of time-extent endpoint
      // For now, use regular query with time filtering
      const fallbackQuery: PathfinderQuery = {
        dateRange: query.windowDateRange,
        timeZone: query.timeZone,
        studentIds: query.studentIds,
        selectedCourse: query.selectedCourse,
        deviceTypes: query.deviceTypes,
        limit: undefined
      };
      return await this.queryDataPoints(fallbackQuery);
      
      /* Commented out until PostgreSQL version is implemented
      const response = await this.client.post('/pathfinder/time-extent', {
        students: query.studentIds,
        full_start_date: query.fullDateRange[0].toISOString(),
        full_end_date: query.fullDateRange[1].toISOString(),
        window_start_date: query.windowDateRange[0].toISOString(),
        window_end_date: query.windowDateRange[1].toISOString(),
        course_id: query.selectedCourse,
        database_tag: query.selectedDatabase,
        device_types: query.deviceTypes
      });
      
      const rawDataPoints = response.data;
      
      // Convert backend response to PathfinderDataPoint format
      const dataPoints: PathfinderDataPoint[] = rawDataPoints.map((point: any) => ({
        id: point.id,
        deviceId: point.device_id,
        deviceType: point.device_type || 'phone',
        studentId: point.entity_id,
        timestamp: new Date(point.timestamp),

        
        coordinates: point.coordinates,
        accuracy: point.accuracy,
        activity: point.activity,
        metadata: point.metadata
      }));
      
      return dataPoints;
      */
      
    } catch (error) {
      console.error('❌ PathfinderApi: Error querying time window data:', error);
      throw new Error('Time window query failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }

  // Query data points (use dedicated Pathfinder endpoint for direct GPS data)
  async queryDataPoints(query: PathfinderQuery): Promise<PathfinderDataPoint[]> {
    try {
      const requestPayload = {
        students: query.studentIds && query.studentIds.length > 0 ? query.studentIds : undefined,
        start_date: this.serializeQueryDate(query.dateRange[0], query.timeZone),
        end_date: this.serializeQueryDate(query.dateRange[1], query.timeZone),
        course_id: query.selectedCourse, // Pass the selected course ID
        database_tag: query.selectedDatabase, // Pass the selected database tag
        device_types: query.deviceTypes,
        limit: query.limit  // No default limit - load all data
      };
      
      const response = await this.client.post('/pathfinder-postgres/postgres-query', requestPayload);
      const rawDataPoints = response.data;
      
      // Debug: Log raw timestamp format from first point
      // Debug-only timestamp logging (enable via localStorage.setItem('debug:api','1'))
      if (rawDataPoints && rawDataPoints.length > 0) {
        debugLog('api', '📅 [queryDataPoints] First raw timestamp:', rawDataPoints[0].timestamp, 'type:', typeof rawDataPoints[0].timestamp);
        debugLog('api', '📅 [queryDataPoints] Parsed as Date:', new Date(rawDataPoints[0].timestamp).toISOString());
        debugLog('api', '📅 [queryDataPoints] Local time:', new Date(rawDataPoints[0].timestamp).toLocaleString());
      }
      
      const dataPoints: PathfinderDataPoint[] = rawDataPoints.map((point: any, index: number) => {
        // Handle coordinates - PostgreSQL backend returns separate lat/lon fields
        let coordinates: [number, number];
        if (point.coordinates && Array.isArray(point.coordinates) && point.coordinates.length >= 2) {
          coordinates = [parseFloat(point.coordinates[0]), parseFloat(point.coordinates[1])];
        } else if (point.latitude !== undefined && point.longitude !== undefined) {
          coordinates = [parseFloat(point.latitude), parseFloat(point.longitude)];
        } else {
          console.error('⚠️ Point missing coordinates:', point);
          coordinates = [0, 0]; // Default fallback
        }
        
        return {
          id: point.id || `point-${index}`,
          deviceId: point.device_id || `${point.student_id}-${point.device_type || 'phone'}`,
          deviceType: point.device_type || 'phone',
          studentId: point.student_id || point.entity_id,
          timestamp: new Date(point.timestamp),
          coordinates: coordinates,
          accuracy: point.accuracy,
          speed: point.speed != null ? parseFloat(point.speed) : undefined, // Speed in km/h
          bearing: point.bearing != null ? parseFloat(point.bearing) : undefined, // Heading in degrees
          activity: point.activity,
          metadata: {
            altitude: point.altitude,
            course_id: point.course_id
          }
        };
      });
      
      return dataPoints;
      
    } catch (error) {
      console.error('❌ PathfinderApi: Error querying GPS data:', error);
      console.error('❌ Full error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        response: (error as any)?.response?.data,
        status: (error as any)?.response?.status,
        query: query
      });
      
      // No fallback to fake data - we want to see and fix the real API issues
      
      // Instead of returning empty array, rethrow the error to see what's failing
      throw new Error(`Pathfinder API query failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // SET.NET 8 Style Co-traveler analysis using loaded data context
  async analyzeCoTravelers(
    params: CoTravelerAnalysisParams
  ): Promise<CoTravelerResult[]> {
    try {
      // Step 1: Use the LOADED data context (same as what's displayed on the map)
      // Query with the same course ID context that was used to load the primary data
      const primaryStudentIds = Array.from(new Set((params.deviceIds || []).map((d) => this.extractStudentId(d))));
      const primaryDataPoints = await this.queryDataPoints({
        dateRange: params.dateRange,
        timeZone: params.timeZone,
        studentIds: primaryStudentIds,
        selectedCourse: params.courseId, // Use same course/database context
        selectedDatabase: params.databaseTag,
        limit: undefined
      });

      if (primaryDataPoints.length === 0) {
        return [];
      }

      // Step 2: SET.NET 8 Method - Create cross-area searches around each primary GPS point
      // Each GPS point becomes a circular search area with the specified radius
      const results: CoTravelerResult[] = [];
      const coTravelerEncounters = new Map<string, CoTravelerEncounter[]>();

      // Step 3: For each primary GPS point, query for other entities within the cross-area
      // This mimics SET.NET 8's crossAreaClass approach with circular geometry searches

      // Get all entities data for the same time period and same database/course context
      // This ensures we're searching in the same database/course that was loaded (SET.NET 8 method)
      const allEntitiesData = await this.queryDataPoints({
        dateRange: params.dateRange,
        timeZone: params.timeZone,
        // Omit studentIds entirely to trigger course-wide query on backend
        selectedCourse: params.courseId, // CRITICAL: Use same course/database context
        selectedDatabase: params.databaseTag,
        limit: undefined
      });

      // Step 4: For each primary GPS point, find nearby co-travelers (SET.NET 8 spatial method)
      for (const primaryPoint of primaryDataPoints) {
        // Filter all entity points to find those within proximity and time thresholds
        const coTravelerPoints = allEntitiesData.filter(point => {
          // Exclude primary students (extract student ID from device ID)
          const pointStudentId = this.extractStudentId(point.deviceId);
          if (primaryStudentIds.includes(pointStudentId)) {
            return false;
          }
          
          // Check temporal proximity first (faster filter)
          const timeDiff = Math.abs(primaryPoint.timestamp.getTime() - point.timestamp.getTime()) / (1000 * 60);
          if (timeDiff > params.timeThreshold) {
            return false;
          }
          
          // Check spatial proximity
          const distance = this.calculateDistance(
            primaryPoint.coordinates[0], primaryPoint.coordinates[1],
            point.coordinates[0], point.coordinates[1]
          );
          
          return distance <= params.proximityThreshold;
        });
        
        // Process encounters for each co-traveler device found
        coTravelerPoints.forEach(coTravelerPoint => {
          const deviceId = coTravelerPoint.deviceId;
          
          if (!coTravelerEncounters.has(deviceId)) {
            coTravelerEncounters.set(deviceId, []);
          }
          
          const distance = this.calculateDistance(
            primaryPoint.coordinates[0], primaryPoint.coordinates[1],
            coTravelerPoint.coordinates[0], coTravelerPoint.coordinates[1]
          );
          const timeDiff = Math.abs(primaryPoint.timestamp.getTime() - coTravelerPoint.timestamp.getTime()) / (1000 * 60);
          
          // Calculate confidence based on proximity and time alignment
          const proximityConfidence = Math.max(0, 1 - (distance / params.proximityThreshold));
          const temporalConfidence = Math.max(0, 1 - (timeDiff / params.timeThreshold));
          const confidence = (proximityConfidence + temporalConfidence) / 2;
          
          const encounter: CoTravelerEncounter = {
            timestamp: primaryPoint.timestamp,
            location: primaryPoint.coordinates,
            duration: Math.max(1, params.timeThreshold - timeDiff),
            proximity: distance,
            confidence,
            activity: primaryPoint.activity,
            locationName: this.getLocationName(primaryPoint.coordinates)
          };
          
          coTravelerEncounters.get(deviceId)!.push(encounter);
        });
      }
      
      // Step 4: Process encounters into co-traveler results
      for (const [coTravelerDeviceId, encounters] of Array.from(coTravelerEncounters.entries())) {
        const totalDuration = encounters.reduce((sum, e) => sum + e.duration, 0);

        // Calculate advanced metrics
        const averageProximity = encounters.reduce((sum, e) => sum + e.proximity, 0) / encounters.length;
        const confidenceScore = encounters.reduce((sum, e) => sum + e.confidence, 0) / encounters.length;

        // Calculate temporal and spatial overlap (simplified for spatial proximity approach)
        const encounterTimes = encounters.map(e => e.timestamp.getTime());
        const primaryTimes = primaryDataPoints.map(p => p.timestamp.getTime());
        const temporalOverlap = this.calculateSimpleTemporalOverlap(encounterTimes, primaryTimes);
        
        // Spatial clustering for frequent locations (always enabled)
        const locationClusters = new Map<string, CoTravelerEncounter[]>();
        encounters.forEach(encounter => {
          const clusterKey = this.getLocationClusterKey(encounter.location, 50); // 50m clusters
          if (!locationClusters.has(clusterKey)) {
            locationClusters.set(clusterKey, []);
          }
          locationClusters.get(clusterKey)!.push(encounter);
        });

        // Generate frequent locations
        const frequentLocations = Array.from(locationClusters.entries())
          .map(([, clusterEncounters]) => {
            const centerLocation = this.calculateClusterCenter(clusterEncounters);
            return {
              location: centerLocation,
              name: this.getLocationName(centerLocation),
              encounters: clusterEncounters.length,
              totalDuration: clusterEncounters.reduce((sum, e) => sum + e.duration, 0)
            };
          })
          .filter(loc => loc.encounters >= 2)
          .sort((a, b) => b.encounters - a.encounters);

        // Create results for each selected device (since we can't reliably map encounters back to specific devices)
        // This ensures the primaryDevice IDs match what the UI expects
        const devicesToUse = params.deviceIds;

        devicesToUse.forEach(primaryDeviceId => {
          results.push({
            primaryDevice: primaryDeviceId,
            coTravelerDevice: coTravelerDeviceId,
            coTravelerEntityType: this.getEntityType(coTravelerDeviceId),
            encounters,
            totalEncounters: encounters.length,
            totalDuration,
            averageProximity,
            confidenceScore,
            temporalOverlap,
            spatialOverlap: 0, // Simplified for spatial proximity approach
            frequentLocations
          });
        });
      }
      
      const sortedResults = results.sort((a, b) => b.confidenceScore - a.confidenceScore);
      return sortedResults;
    } catch (error) {
      console.error('Error analyzing co-travelers:', error);
      return [];
    }
  }

  // Server-side cross-area co-traveler (SET.NET 8 style)
  async analyzeCoTravelersCrossAreas(
    params: CoTravelerAnalysisParams
  ): Promise<CoTravelerResult[]> {
    try {
      // Step 1: Get primary data points to create cross-areas
      // IMPORTANT: Use primaryCourseId for student data (ALWAYS the selected course)
      // courseId is used for co-traveler search (can be undefined for RP/SRP to search all courses)
      const primaryStudentIds = Array.from(new Set((params.deviceIds || []).map((d) => this.extractStudentId(d))));
      const effectivePrimaryCourseId = params.primaryCourseId || params.courseId;
      
      const primaryDataPoints = await this.queryDataPoints({
        dateRange: params.dateRange,
        timeZone: params.timeZone,
        studentIds: primaryStudentIds,
        selectedCourse: effectivePrimaryCourseId,  // Use primary course, NOT co-traveler course filter
        selectedDatabase: params.databaseTag,
        limit: undefined
      });

      if (primaryDataPoints.length === 0) {
        return [];
      }

      // EXCLUSION LOGIC: Filter out primary GPS points within Davis-Monthan AFB before creating cross-areas
      const davisMonthanPolygon: [number, number][] = [
        [32.1899942, -110.8668227],  // [lat, lon] format like [32.1759060, -110.8666880],
        [32.1804096, -110.8931131],
        [32.1944209, -110.8933825]
      ];

      const isPointInPolygon = (point: [number, number], polygon: [number, number][]): boolean => {
        // FIX: point is [lat, lon] but we need [lon, lat] for x,y coordinates
        const [lat, lon] = point;  // Input is [latitude, longitude]
        const x = lon;  // longitude is x (east-west)
        const y = lat;  // latitude is y (north-south)
        let inside = false;
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
          const [yi, xi] = polygon[i];  // polygon is also [lat, lon] so swap for x,y
          const [yj, xj] = polygon[j];  // polygon is also [lat, lon] so swap for x,y
          const intersect = ((yi > y) !== (yj > y)) && 
                           (x < (xj - xi) * (y - yi) / (yj - yi + Number.EPSILON) + xi);
          if (intersect) inside = !inside;
        }
        return inside;
      };

      // Test the exclusion function with multiple known points
      const testPoints = [
        { coords: [32.18, -110.88], expected: true, desc: 'Inside Davis-Monthan AFB' },
        { coords: [32.185, -110.875], expected: true, desc: 'Also inside Davis-Monthan' },
        { coords: [32.22, -110.95], expected: false, desc: 'North of Davis-Monthan' },
        { coords: [32.15, -110.85], expected: false, desc: 'South of Davis-Monthan' },
        { coords: [32.20, -110.85], expected: false, desc: 'East of Davis-Monthan' }
      ];
      
      testPoints.forEach(test => {
        const result = isPointInPolygon(test.coords as [number, number], davisMonthanPolygon);
        const _status = result === test.expected ? '✅' : '❌';
        void _status;
      });

      // EXCLUSION TOGGLE: Set to false to disable all exclusion filtering for testing
      // TO RE-ENABLE: Change both ENABLE_EXCLUSION_ZONE here and ENABLE_DISPLAY_EXCLUSION in PathfinderPage.tsx to true
      const ENABLE_EXCLUSION_ZONE = false; // TEMPORARILY DISABLED to create cross-areas everywhere
      
      // APPROACH: Filter out primary device points that are IN Davis-Monthan
      // This prevents searching for co-travelers inside the military base
      const filteredPoints = ENABLE_EXCLUSION_ZONE ? 
        primaryDataPoints.filter((point) => {
          const lat = point.coordinates[0];
          const lon = point.coordinates[1];
          const isExcluded = isPointInPolygon([lat, lon], davisMonthanPolygon);
          
          return !isExcluded; // Keep points that are NOT in Davis-Monthan
        }) : 
        primaryDataPoints; // Use ALL points when exclusion is disabled
      
      // APPROACH: If all points are in Davis-Monthan, return no results
      if (ENABLE_EXCLUSION_ZONE && filteredPoints.length === 0) {
        return []; // Return empty results - no co-travelers if primary never left exclusion zone
      }
      
      // Use only the filtered points (outside exclusion zones)
      const pointsToUse = filteredPoints;

      // Step 2: Create cross-areas around each filtered primary GPS point (client-side clustering to reduce payload)
      // Use finer grid and shorter time buckets to capture more unique areas
      const gridPrecision = 2e-4; // ~22m at equator (finer than before)
      const timeBucketSeconds = 120; // 2-minute buckets (finer than before)
      const seen = new Set<string>();
      const crossAreas: CrossArea[] = [];
      for (const point of pointsToUse) {
        const lat = point.coordinates[0];
        const lon = point.coordinates[1];
        if (!(lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180)) continue;
        const tBucket = Math.floor(point.timestamp.getTime() / 1000 / timeBucketSeconds);
        const latB = Math.round(lat / gridPrecision) * gridPrecision;
        const lonB = Math.round(lon / gridPrecision) * gridPrecision;
        const key = tBucket + '_' + latB.toFixed(4) + '_' + lonB.toFixed(4);
        if (seen.has(key)) continue;
        seen.add(key);
        // DISABLED: No frontend exclusion - backend handles all exclusion logic
        // We WANT to create cross-areas everywhere the primary device went
        
        crossAreas.push({
          latitude: lat,
          longitude: lon,
          radius: Math.max(10, Math.min(1000, params.proximityThreshold)),
          start_date: new Date(point.timestamp.getTime() - params.timeThreshold * 60 * 1000).toISOString(),
          end_date: new Date(point.timestamp.getTime() + params.timeThreshold * 60 * 1000).toISOString()
        });
        // High limit to ensure comprehensive route coverage
        // With 2-min buckets and ~22m grid, this allows thorough co-traveler detection
        if (crossAreas.length >= 5000) break;
      }
      
      if (crossAreas.length === 0) {
        return await this.analyzeCoTravelers(params);
      }

      // Step 3: Call server-side endpoint
      const request: CoTravelerRequest = {
        course_id: params.courseId,
        database_tag: params.databaseTag,
        primary_ids: params.deviceIds,
        cross_area_radius: params.proximityThreshold,
        areas: crossAreas,
        entity_types_filter: params.entityTypesFilter  // Pass entity type filter for DB-level optimization
      };
      
      const response = await this.client.post<CoTravelerResponseDTO>('/pathfinder-postgres/co-travelers', request, {
        timeout: 180000 // 3 minutes timeout to handle large co-traveler analyses
      });
      
      // Step 4: Convert server response to CoTravelerResult format
      const results: CoTravelerResult[] = [];
      const matchesByCoTraveler = new Map<string, CoTravelerMatchDTO[]>();

      // Group matches by co-traveler entity
      response.data.matches.forEach((match: CoTravelerMatchDTO) => {
        const entityId = match.entity_id || match.device_id || 'Unknown';
        if (!matchesByCoTraveler.has(entityId)) {
          matchesByCoTraveler.set(entityId, []);
        }
        matchesByCoTraveler.get(entityId)!.push(match);
      });

      // Convert to CoTravelerResult format
      matchesByCoTraveler.forEach((matches, coTravelerDeviceId) => {
        // Convert backend matches to encounters
        const encounters: CoTravelerEncounter[] = matches.map(match => ({
          timestamp: new Date(match.timestamp),
          location: match.coordinates,
          duration: params.timeThreshold, // Use the configured time threshold
          proximity: match.distance_m || 0,
          confidence: Math.max(0, 1 - (match.distance_m || 0) / params.proximityThreshold),
          activity: undefined,
          locationName: this.getLocationName(match.coordinates)
        }));

        // Calculate metrics
        const totalDuration = encounters.reduce((sum, e) => sum + e.duration, 0);
        const averageProximity = encounters.reduce((sum, e) => sum + e.proximity, 0) / encounters.length;
        const confidenceScore = encounters.reduce((sum, e) => sum + e.confidence, 0) / encounters.length;

        // Calculate frequent locations (group nearby encounters)
        const locationGroups = new Map<string, { location: [number, number], name?: string, encounters: number, totalDuration: number }>();
        encounters.forEach(encounter => {
          // Use a simple grid-based grouping (round to ~100m precision)
          const gridKey = `${encounter.location[0].toFixed(3)},${encounter.location[1].toFixed(3)}`;
          
          if (!locationGroups.has(gridKey)) {
            locationGroups.set(gridKey, {
              location: encounter.location,
              name: encounter.locationName,
              encounters: 0,
              totalDuration: 0
            });
          }
          
          const group = locationGroups.get(gridKey)!;
          group.encounters++;
          group.totalDuration += encounter.duration;
        });
        
        const frequentLocations = Array.from(locationGroups.values())
          .sort((a, b) => b.encounters - a.encounters)
          .slice(0, 10); // Keep top 10 locations

        // Get entity type from backend response (first match has the type)
        // Falls back to pattern matching if backend didn't provide entity_type
        const backendEntityType = matches[0]?.entity_type;
        const entityType = this.mapEntityType(backendEntityType) || this.getEntityType(coTravelerDeviceId);

        // Create one result per co-traveler (backend already analyzed all primary devices together)
        results.push({
          primaryDevice: params.deviceIds[0], // Use first device as primary
          coTravelerDevice: coTravelerDeviceId,
          coTravelerEntityType: entityType,
          encounters,
          totalEncounters: encounters.length,
          totalDuration,
          averageProximity,
          confidenceScore,
          temporalOverlap: 0, // Simplified for server-side analysis
          spatialOverlap: 0, // Simplified for server-side analysis
          frequentLocations
        });
      });

      // Sort by confidence score descending
      results.sort((a, b) => b.confidenceScore - a.confidenceScore);
      
      return results;

    } catch (error: any) {
      console.error('Server-side co-traveler analysis failed:', error);
      throw new Error('Server-side co-traveler analysis failed: ' + error.message);
    }
  }

  // Legacy method for backward compatibility
  async analyzeCoTravelersLegacy(
    deviceIds: string[],
    dateRange: [Date, Date],
    proximityThreshold: number = 100,
    timeThreshold: number = 30
  ): Promise<CoTravelerResult[]> {
    return this.analyzeCoTravelers({
      deviceIds,
      dateRange,
      proximityThreshold,
      timeThreshold
    });
  }

  // Pattern of Life analysis (use real dwell and route data)
  async analyzePatternOfLife(
    deviceId: string,
    dateRange: [Date, Date]
  ): Promise<PatternOfLifeResult> {
    try {
      // Get data points for the device
      const dataPoints = await this.queryDataPoints({
        dateRange,
        studentIds: [deviceId],
        limit: undefined  // No limit - load all data
      });
      
      // Analyze physical patterns from dwell locations
      const dwellPoints = dataPoints.filter(p => p.activity === 'dwell');
      const dwellLocationMap = new Map<string, {
        location: [number, number];
        visits: number;
        totalDuration: number;
        address?: string;
      }>();
      
      dwellPoints.forEach(point => {
        const key = point.coordinates[0].toFixed(4) + '_' + point.coordinates[1].toFixed(4);
        if (!dwellLocationMap.has(key)) {
          dwellLocationMap.set(key, {
            location: point.coordinates,
            visits: 0,
            totalDuration: 0,
            address: point.metadata?.placeName || 'Unknown Location'
          });
        }
        const dwell = dwellLocationMap.get(key)!;
        dwell.visits++;
        dwell.totalDuration += point.metadata?.totalDuration || 30; // Default 30 minutes
      });
      
      // Convert to array and calculate averages
      const dwellLocations = Array.from(dwellLocationMap.values()).map(dwell => ({
        ...dwell,
        averageDuration: dwell.totalDuration / dwell.visits
      }));
      
      // Analyze movement patterns
      const movementPoints = dataPoints.filter(p => p.activity === 'movement').sort((a, b) => 
        a.timestamp.getTime() - b.timestamp.getTime()
      );
      
      const movementPatterns: PatternOfLifeResult['patterns']['physical']['movementPatterns'] = [];
      for (let i = 0; i < movementPoints.length - 1; i++) {
        const from = movementPoints[i];
        const to = movementPoints[i + 1];
        const timeDiff = (to.timestamp.getTime() - from.timestamp.getTime()) / (1000 * 60); // minutes
        
        if (timeDiff > 0 && timeDiff < 120) { // Only consider movements within 2 hours
          movementPatterns.push({
            fromLocation: from.coordinates,
            toLocation: to.coordinates,
            frequency: 1,
            averageTime: timeDiff
          });
        }
      }
      
      // Generate hourly activity patterns based on data points
      const hourlyActivity = Array.from({ length: 24 }, (_, hour) => {
        const count = dataPoints.filter(p => p.timestamp.getHours() === hour).length;
        return { hour, count };
      });
      
      return {
        deviceId,
        patterns: {
          digital: {
            callActivity: hourlyActivity.map(h => ({ ...h, count: Math.floor(h.count * 0.3) })),
            smsActivity: hourlyActivity.map(h => ({ ...h, count: Math.floor(h.count * 0.5) })),
            webActivity: hourlyActivity.map(h => ({ ...h, count: Math.floor(h.count * 0.8) }))
          },
          physical: {
            dwellLocations,
            movementPatterns
          }
        }
      };
    } catch (error) {
      console.error('Error analyzing pattern of life:', error);
      return {
        deviceId,
        patterns: {
          digital: {
            callActivity: [],
            smsActivity: [],
            webActivity: []
          },
          physical: {
            dwellLocations: [],
            movementPatterns: []
          }
        }
      };
    }
  }

  // Search functionality (search through real analysis data)
  async searchData(searchTerm: string, filters?: Partial<PathfinderQuery>): Promise<PathfinderDataPoint[]> {
    try {
      // Get all available data points
      const allDataPoints = await this.queryDataPoints({
        dateRange: filters?.dateRange || [new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), new Date()],
        studentIds: filters?.studentIds,
        deviceTypes: filters?.deviceTypes,
        limit: undefined  // No limit - load all data
      });
      
      // Filter data points based on search term
      const searchLower = searchTerm.toLowerCase();
      const filteredPoints = allDataPoints.filter(point => {
        return (
          point.activity?.toLowerCase().includes(searchLower) ||
          point.metadata?.placeName?.toLowerCase().includes(searchLower) ||
          point.deviceId.toLowerCase().includes(searchLower) ||
          point.id.toLowerCase().includes(searchLower)
        );
      });
      
      return filteredPoints;
    } catch (error) {
      console.error('Error searching data:', error);
      return [];
    }
  }

  // Generate route lines from data points for visualization, separated by device type
  generateRoutes(dataPoints: PathfinderDataPoint[]): PathfinderRoute[] {
    // Group points by student ID and device type.
    // Normalize deviceType the same way PathfinderPage does so the route's
    // deviceId matches selectedDevices keys (e.g. backend "cellphone" /
    // "samsungphone" / "smartphone" all collapse to "phone"). Without this,
    // route filters drop the polyline even though points still render.
    const normalizeDeviceType = (raw: string | undefined): string => {
      const lower = (raw || 'unknown').toLowerCase();
      if (lower.includes('phone')) return 'phone';
      return lower;
    };

    // Drop points with invalid coordinates up front. Mapbox silently
    // discards an entire LineString feature if any vertex is NaN / out of
    // bounds, which is why the points layer (which validates per-feature)
    // can render clusters while the route polyline disappears.
    const isValidCoord = (c: any): c is [number, number] => {
      if (!c || !Array.isArray(c) || c.length < 2) return false;
      const lat = c[0];
      const lon = c[1];
      if (typeof lat !== 'number' || typeof lon !== 'number') return false;
      if (!isFinite(lat) || !isFinite(lon)) return false;
      if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return false;
      // Treat the (0,0) "null island" fallback as invalid too.
      if (lat === 0 && lon === 0) return false;
      return true;
    };

    const deviceGroups: Record<string, PathfinderDataPoint[]> = {};

    dataPoints.forEach(point => {
      if (!isValidCoord(point.coordinates as any)) return;
      const studentId = point.studentId || point.deviceId;
      const normalizedType = normalizeDeviceType(point.deviceType);
      const deviceKey = `${studentId}__${normalizedType}`;
      if (!deviceGroups[deviceKey]) {
        deviceGroups[deviceKey] = [];
      }
      deviceGroups[deviceKey].push(point);
    });

    const routes: PathfinderRoute[] = [];
    
    // Device type colors matching style
    const deviceTypeColors = {
      watch: '#2196f3',    // Blue
      vehicle: '#9c27b0',  // Purple
      phone: '#4caf50',    // Green
      unknown: '#757575'   // Gray
    };

    Object.entries(deviceGroups).forEach(([deviceKey, points]) => {
      if (points.length < 2) return; // Need at least 2 points for a route

      // deviceKey format: "<studentId>__<normalizedDeviceType>"
      const sepIdx = deviceKey.lastIndexOf('__');
      const studentId = sepIdx > 0 ? deviceKey.slice(0, sepIdx) : deviceKey;
      const deviceTypeStr = sepIdx > 0 ? deviceKey.slice(sepIdx + 2) : 'unknown';
      const deviceType = deviceTypeStr as 'watch' | 'vehicle' | 'phone';
      const originalDeviceId = points[0].deviceId;
      const uniqueDeviceId = studentId + '-' + deviceType;

      // Sort points by timestamp
      const sortedPoints = points.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
      
      // Calculate total distance with validation
      let totalDistance = 0;
      const coordinates: [number, number][] = sortedPoints.map(point => point.coordinates);
      
      for (let i = 1; i < coordinates.length; i++) {
        const lat1 = coordinates[i-1][0], lon1 = coordinates[i-1][1];
        const lat2 = coordinates[i][0], lon2 = coordinates[i][1];
        
        // VALIDATE: Skip invalid coordinates (null, NaN, out of bounds)
        if (!lat1 || !lon1 || !lat2 || !lon2 ||
            !isFinite(lat1) || !isFinite(lon1) || !isFinite(lat2) || !isFinite(lon2) ||
            Math.abs(lat1) > 90 || Math.abs(lat2) > 90 ||
            Math.abs(lon1) > 180 || Math.abs(lon2) > 180) {
          continue;
        }
        
        const segmentDistance = this.calculateDistance(lat1, lon1, lat2, lon2);
        
        // VALIDATE: Speed-based filter - allows flights but catches GPS teleportation
        // Max realistic speed: 1000 km/h (620 mph) - faster than any commercial jet
        // If implied speed > 1000 km/h, it's GPS noise, not real travel
        const timeDiffMs = sortedPoints[i].timestamp.getTime() - sortedPoints[i-1].timestamp.getTime();
        const timeDiffHours = timeDiffMs / (1000 * 60 * 60);
        
        if (timeDiffHours > 0) {
          const impliedSpeedKmh = (segmentDistance / 1000) / timeDiffHours;
          // 1000 km/h max allows: driving, flights, but not GPS teleportation
          if (impliedSpeedKmh > 1000) {
            console.warn(`[PathfinderApi] Skipping GPS noise: ${impliedSpeedKmh.toFixed(0)} km/h implied speed (${(segmentDistance/1000).toFixed(1)}km in ${(timeDiffHours*60).toFixed(1)}min)`);
            continue;
          }
        } else if (segmentDistance > 100) {
          // Same timestamp but moved > 100m = duplicate/bad data
          continue;
        }
        
        totalDistance += segmentDistance;
      }

      const route: PathfinderRoute = {
        id: studentId + '-' + deviceType + '-' + originalDeviceId,
        studentId,
        deviceId: uniqueDeviceId,
        deviceType,
        points: sortedPoints,
        coordinates,
        startTime: sortedPoints[0].timestamp,
        endTime: sortedPoints[sortedPoints.length - 1].timestamp,
        totalDistance,
        color: deviceTypeColors[deviceType] || deviceTypeColors.unknown
      };

      routes.push(route);
    });

    return routes;
  }

  // Helper function to extract device type from device ID
  extractDeviceType(deviceId: string): 'watch' | 'vehicle' | 'phone' {
    if (deviceId.includes('-watch')) return 'watch';
    if (deviceId.includes('-vehicle')) return 'vehicle';
    if (deviceId.includes('-phone')) return 'phone';
    // Default fallback
    return 'phone';
  }

  // Helper function to extract student ID from device ID
  extractStudentId(deviceId: string): string {
    // Handle new format with spaces: "Trainee-09 - Phone" -> "Trainee-09"
    if (deviceId.includes(' - ')) {
      return deviceId.split(' - ')[0];
    }
    // Handle old format without spaces: "Student-07-phone" -> "Student-07"
    return deviceId.replace(/-watch|-vehicle|-phone|_device/g, '');
  }

  // Calculate distance between two points in meters (Haversine formula)
  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI/180;
    const φ2 = lat2 * Math.PI/180;
    const Δφ = (lat2-lat1) * Math.PI/180;
    const Δλ = (lon2-lon1) * Math.PI/180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c;
  }

  // Helper methods for enhanced co-traveler analysis
  
  // Map backend entity_type to frontend type
  private mapEntityType(backendType: string | undefined): 'Student' | 'SRP' | 'RP' | 'Unknown' | null {
    if (!backendType) return null;
    const upper = backendType.toUpperCase();
    if (upper === 'SRP' || upper.includes('SRP')) return 'SRP';
    if (upper === 'RP' || upper.includes('ROLE')) return 'RP';
    if (upper === 'STUDENT' || upper.includes('TRAINEE')) return 'Student';
    return null; // Unknown type, fall back to pattern matching
  }
  
  private getEntityType(deviceId: string): 'Student' | 'SRP' | 'RP' | 'Unknown' {
    // Detect entity type by splitting the deviceId on dash/underscore/space
    // and matching RP/SRP as discrete tokens. Substring matching previously
    // misclassified freeform names containing the letters "RP" — e.g.
    // "PurpleHaze" (matches inside "PuRPle") was wrongly detected as RP.
    const upperDeviceId = deviceId.toUpperCase();
    const tokens = upperDeviceId.split(/[-_\s]+/);

    if (tokens.includes('SRP')) {
      return 'SRP';
    }
    if (tokens.includes('RP')) {
      return 'RP';
    }
    // Default to Student for typical student device patterns
    if (upperDeviceId.match(/^[A-Z0-9]+$/) || tokens.includes('STUDENT') || tokens.includes('STU') || tokens.includes('TRAINEE')) {
      return 'Student';
    }

    return 'Unknown';
  }

  private getLocationName(coordinates: [number, number]): string {
    // Simple location naming - could be enhanced with reverse geocoding
    return 'Location ' + coordinates[1].toFixed(4) + ', ' + coordinates[0].toFixed(4);
  }

  private getLocationClusterKey(coordinates: [number, number], clusterRadius: number): string {
    // Create a grid-based clustering key
    const gridSize = clusterRadius / 111320; // Approximate degrees per meter
    const gridLat = Math.floor(coordinates[1] / gridSize) * gridSize;
    const gridLon = Math.floor(coordinates[0] / gridSize) * gridSize;
    return gridLat.toFixed(6) + ',' + gridLon.toFixed(6);
  }

  private calculateClusterCenter(encounters: CoTravelerEncounter[]): [number, number] {
    if (encounters.length === 0) return [0, 0];

    const avgLon = encounters.reduce((sum, e) => sum + e.location[0], 0) / encounters.length;
    const avgLat = encounters.reduce((sum, e) => sum + e.location[1], 0) / encounters.length;

    return [avgLon, avgLat];
  }

  private calculateSimpleTemporalOverlap(encounterTimes: number[], primaryTimes: number[]): number {
    if (encounterTimes.length === 0 || primaryTimes.length === 0) return 0;

    // Use forEach to avoid stack overflow on large datasets
    let encMin = Infinity, encMax = -Infinity;
    encounterTimes.forEach(t => {
      if (t < encMin) encMin = t;
      if (t > encMax) encMax = t;
    });
    let primMin = Infinity, primMax = -Infinity;
    primaryTimes.forEach(t => {
      if (t < primMin) primMin = t;
      if (t > primMax) primMax = t;
    });
    
    const encounterRange = { min: encMin, max: encMax };
    const primaryRange = { min: primMin, max: primMax };

    const overlapStart = Math.max(encounterRange.min, primaryRange.min);
    const overlapEnd = Math.min(encounterRange.max, primaryRange.max);

    if (overlapStart >= overlapEnd) return 0;

    const overlapDuration = overlapEnd - overlapStart;
    const totalDuration = Math.max(encounterRange.max, primaryRange.max) - Math.min(encounterRange.min, primaryRange.min);

    return (overlapDuration / totalDuration) * 100;
  }

  // Dwell data functionality - query dwell locations
  async getDwells(params: {
    courseId?: string;
    deviceType?: string;
    days?: number;
    startDate?: Date;
    endDate?: Date;
    timeZone?: string;
    limit?: number;
    studentsOnly?: boolean;
    center?: [number, number]; // [lat, lon]
    radiusMeters?: number;
  }): Promise<any[]> {
    try {
      const queryParams: any = {
        course_id: params.courseId,
        device_type: params.deviceType || 'all',
        limit: params.limit || 100,
      };
      
      // Filter to Students only if requested
      if (params.studentsOnly) {
        queryParams.entity_type = 'Student';
      }
      
      // Use date range if provided, otherwise use days
      if (params.startDate && params.endDate) {
        queryParams.start_date = this.serializeQueryDate(params.startDate, params.timeZone);
        queryParams.end_date = this.serializeQueryDate(params.endDate, params.timeZone);
      } else {
        queryParams.days = params.days || 7;
      }

      // Optional spatial filter (server-side)
      if (params.center && typeof params.radiusMeters === 'number') {
        queryParams.center_lat = params.center[0];
        queryParams.center_lon = params.center[1];
        queryParams.radius_meters = params.radiusMeters;
      }
      
      const response = await this.client.get('/cop-dwells', {
        params: queryParams
      });
      
      // Return the dwells array from the response
      return response.data.dwells || [];
    } catch (error: any) {
      console.error('❌ Failed to fetch dwell data:', error);
      throw new Error(`Failed to fetch dwell data: ${error.response?.data?.message || error.message}`);
    }
  }

  // Area search functionality - query devices within a circular area
  async queryAreaSearch(params: {
    center: [number, number]; // [lat, lon]
    radius: number; // meters
    dateRange: [Date, Date];
    timeZone?: string;
    selectedCourse?: string;
    excludeStudentIds?: string[]; // Students to exclude from results
    studentsOnly?: boolean; // Filter to Students only (exclude RP, SRP)
  }): Promise<PathfinderDataPoint[]> {
    try {
      const response = await this.client.post('/pathfinder-postgres/area-search', {
        center_lat: params.center[0],
        center_lon: params.center[1], 
        radius_meters: params.radius,
        start_date: this.serializeQueryDate(params.dateRange[0], params.timeZone),
        end_date: this.serializeQueryDate(params.dateRange[1], params.timeZone),
        course_id: params.selectedCourse,
        exclude_student_ids: params.excludeStudentIds || [],
        students_only: params.studentsOnly || false
      });
      
      // Transform response to PathfinderDataPoint format
      return response.data.map((point: any) => {
        // Handle coordinates - backend sends as array or separate lat/lon fields
        let coordinates: [number, number];
        if (point.coordinates && Array.isArray(point.coordinates) && point.coordinates.length >= 2) {
          coordinates = [parseFloat(point.coordinates[0]), parseFloat(point.coordinates[1])];
        } else if (point.latitude !== undefined && point.longitude !== undefined) {
          coordinates = [parseFloat(point.latitude), parseFloat(point.longitude)];
        } else {
          console.warn('⚠️ Point missing coordinates:', point);
          coordinates = [0, 0]; // Default fallback
        }
        
        return {
          id: point.id,
          studentId: point.student_id || point.entity_id,
          deviceId: point.device_id || `${point.student_id || point.entity_id}-${point.device_type || 'phone'}`,
          deviceType: point.device_type || 'phone',
          coordinates,
          timestamp: new Date(point.timestamp),
          accuracy: point.accuracy,
          activity: point.activity,
          metadata: {
            altitude: point.altitude,
            speed: point.speed,
            bearing: point.bearing
          }
        };
      });

    } catch (error: any) {
      console.error('❌ Area Search API failed:', error);
      throw new Error(`Area search failed: ${error.response?.data?.message || error.message}`);
    }
  }

  // Generate AI analysis for area search results
  async generateAreaAnalysisAI(params: {
    areaName: string;
    center: [number, number];
    radius: number;
    entitiesCount: number;
    dwellInsights: any[];
    coLocationInsights: any[];
    mapboxContext?: any;
    useFullDetail?: boolean;
    adtechSummary?: Record<string, any> | null;
  }): Promise<{ success: boolean; summary: string; raw_data: any }> {
    try {
      const response = await this.client.post('/pathfinder-postgres/area-analysis-ai', {
        areaName: params.areaName,
        center: params.center,
        radius: params.radius,
        entitiesCount: params.entitiesCount,
        dwellInsights: params.dwellInsights,
        coLocationInsights: params.coLocationInsights,
        mapboxContext: params.mapboxContext,
        useFullDetail: params.useFullDetail,
        adtechSummary: params.adtechSummary ?? undefined,
      });

      return response.data;
    } catch (error: any) {
      console.error('❌ AI analysis API failed:', error);
      throw new Error(`AI analysis failed: ${error.response?.data?.message || error.message}`);
    }
  }

  // Generate AI analysis for Story Mode timeline
  async generateStoryModeAI(params: {
    entities: string[];
    totalEvents: number;
    travelCount: number;
    dwellCount: number;
    totalTravelDistance: number;
    totalTravelTime: number;
    totalDwellTime: number;
    uniqueLocations: string[];
    timelineEvents: any[];
    /** What the analyst wants the summary to concentrate on (optional). */
    focus?: string;
    /** Other datasets loaded on the map (cameras passed, co-travelers, devices …). */
    context?: Record<string, any>;
    /** short = tight briefing; detailed = full structured write-up. */
    detail?: 'short' | 'detailed';
  }): Promise<{ success: boolean; summary: string }> {
    try {
      const response = await this.client.post('/pathfinder-postgres/story-mode-ai', {
        entities: params.entities,
        totalEvents: params.totalEvents,
        travelCount: params.travelCount,
        dwellCount: params.dwellCount,
        totalTravelDistance: params.totalTravelDistance,
        totalTravelTime: params.totalTravelTime,
        totalDwellTime: params.totalDwellTime,
        uniqueLocations: params.uniqueLocations,
        timelineEvents: params.timelineEvents,
        focus: params.focus || '',
        context: params.context ?? {},
        detail: params.detail || 'detailed',
      });

      return response.data;
    } catch (error: any) {
      console.error('❌ Story Mode AI analysis API failed:', error);
      throw new Error(`Story Mode AI analysis failed: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get Digital Pattern of Life data
   */
  async getDigitalPoL(params: {
    course_id?: string;
    entity_ids?: string;
    days?: number;
    start_date?: string;
    end_date?: string;
    activity_type?: string;
    limit?: number;
  }): Promise<any[]> {
    try {
      return await apiService.getDigitalPoL(params);
    } catch (error: any) {
      console.error('❌ Digital PoL API failed:', error);
      throw error;
    }
  }

  // Generate AI analysis for Digital PoL data
  async generateDigitalPoLAI(params: {
    entities: string[];
    totalActivities: number;
    activityBreakdown: Record<string, number>;
    entityBreakdown: Record<string, number>;
    uniqueEntities: number;
    dateRange: { start: string; end: string };
    topApps: Array<{ name: string; count: number; totalTime: number }>;
    topUrls: Array<{ url: string; count: number }>;
    topCallContacts: Array<{ contact: string; count: number; totalDuration: number }>;
    topSmsContacts: Array<{ contact: string; count: number }>;
    peakHour: number | null;
    course: string;
    timespan: number;
  }): Promise<{ success: boolean; summary: string }> {
    try {
      const response = await this.client.post('/pathfinder/digital-pol-ai', {
        entities: params.entities,
        totalActivities: params.totalActivities,
        activityBreakdown: params.activityBreakdown,
        entityBreakdown: params.entityBreakdown,
        uniqueEntities: params.uniqueEntities,
        dateRange: params.dateRange,
        topApps: params.topApps,
        topUrls: params.topUrls,
        topCallContacts: params.topCallContacts,
        topSmsContacts: params.topSmsContacts,
        peakHour: params.peakHour,
        course: params.course,
        timespan: params.timespan,
      });

      return response.data;
    } catch (error: any) {
      console.error('❌ Digital PoL AI analysis API failed:', error);
      throw new Error(`Digital PoL AI analysis failed: ${error.response?.data?.detail || error.message}`);
    }
  }

  /**
   * Get road names for a route using Mapbox Directions API
   * Only sends simplified coordinates (start/end or key waypoints) to minimize API calls
   * Automatically excludes dwell areas from the route
   */
  async getRouteRoads(
    coordinates: [number, number][], // Array of [lat, lng] coordinates
    profile: 'driving' | 'walking' | 'cycling' = 'driving',
    dwellLocations?: Array<{ lat: number; lon: number; radius?: number }> // Optional: dwell areas to exclude
  ): Promise<{ roads: Array<{ name: string; distance: number; duration: number }>; total_distance: number; total_duration: number }> {
    try {
      // Convert [lat, lng] to [lon, lat] for Mapbox API
      const mapboxCoords = coordinates.map(coord => [coord[1], coord[0]]);
      
      // Format dwell locations if provided
      const dwellLocs = dwellLocations?.map(dwell => ({
        lat: dwell.lat,
        lon: dwell.lon,
        radius: dwell.radius || 50 // Default 50m radius
      }));
      
      // Use apiService.client which has authentication interceptors
      const response = await apiService.client.post(
        `/route-geocoding/geocode-route`,
        {
          coordinates: mapboxCoords,
          profile,
          dwell_locations: dwellLocs
        }
      );
      
      return response.data;
    } catch (error: any) {
      console.error('❌ Route geocoding failed:', error);
      // Return empty result instead of throwing - don't break route analysis if geocoding fails
      return { roads: [], total_distance: 0, total_duration: 0 };
    }
  }

  /**
   * Simplified route geocoding - just start and end points
   * Most efficient for API usage
   */
  async getRouteRoadsSimple(
    startLat: number,
    startLon: number,
    endLat: number,
    endLon: number,
    profile: 'driving' | 'walking' | 'cycling' = 'driving'
  ): Promise<{ roads: Array<{ name: string; distance: number; duration: number }>; total_distance: number; total_duration: number }> {
    try {
      // Use apiService.client which has authentication interceptors
      const response = await apiService.client.post(
        `/route-geocoding/geocode-route-simple`,
        null,
        {
          params: {
            start_lat: startLat,
            start_lon: startLon,
            end_lat: endLat,
            end_lon: endLon,
            profile
          }
        }
      );
      
      return response.data;
    } catch (error: any) {
      console.error('❌ Route geocoding failed:', error);
      return { roads: [], total_distance: 0, total_duration: 0 };
    }
  }
  async getSignalEnvironmentArea(params: {
    center: [number, number];
    radiusMeters: number;
    dateRange: [Date, Date];
    timeZone?: string;
    signalTypes?: string[];
    limitPerType?: number;
  }): Promise<SignalEnvironmentData> {
    const response = await this.client.post('/pathfinder-postgres/signal-environment-area', {
      center_lat: params.center[0],
      center_lon: params.center[1],
      radius_meters: params.radiusMeters,
      start_date: this.serializeQueryDate(params.dateRange[0], params.timeZone),
      end_date: this.serializeQueryDate(params.dateRange[1], params.timeZone),
      signal_types: params.signalTypes ?? ['wifi', 'bluetooth', 'cellular', 'gnss'],
      limit_per_type: params.limitPerType ?? 5000,
    });
    return response.data;
  }

  async compareSignalsAcrossAreas(params: {
    areas: Array<{ id: string; center: [number, number]; radiusMeters: number }>;
    dateRange: [Date, Date];
    timeZone?: string;
    mode: 'intersection' | 'union';
    signalTypes?: string[];
    limitPerType?: number;
  }): Promise<SignalCompareResult> {
    const response = await this.client.post('/pathfinder-postgres/signal-compare-areas', {
      areas: params.areas.map(a => ({
        id: a.id,
        center_lat: a.center[0],
        center_lon: a.center[1],
        radius_meters: a.radiusMeters,
      })),
      start_date: this.serializeQueryDate(params.dateRange[0], params.timeZone),
      end_date: this.serializeQueryDate(params.dateRange[1], params.timeZone),
      mode: params.mode,
      signal_types: params.signalTypes ?? ['wifi', 'bluetooth', 'cellular'],
      limit_per_type: params.limitPerType ?? 500,
    });
    return response.data;
  }

  // ==========================================================================
  // AdTech (Venntel / GeoNeutron) — opt-in module gated in Settings -> Modules.
  // Callers MUST check getAdtechStatus().available (and the 'adtech' feature
  // flag) before surfacing any adtech UI. Results are not persisted server-side.
  // ==========================================================================

  async getAdtechStatus(): Promise<AdtechStatus> {
    const response = await this.client.get('/pathfinder-postgres/adtech/status');
    return response.data;
  }

  async analyzeAdtechCoTravelers(params: {
    entityIds: string[];
    dateRange: [Date, Date];
    timeZone?: string;
    radiusMeters?: number;
    timeThresholdMinutes?: number;
    minEncounters?: number;
    minDurationMinutes?: number;
    limit?: number;
  }): Promise<AdtechCoTravelerResult> {
    try {
      const response = await this.client.post(
        '/pathfinder-postgres/adtech-co-travelers',
        {
          entity_ids: params.entityIds,
          start_date: this.serializeQueryDate(params.dateRange[0], params.timeZone),
          end_date: this.serializeQueryDate(params.dateRange[1], params.timeZone),
          radius_meters: params.radiusMeters ?? 250,
          time_threshold_minutes: params.timeThresholdMinutes ?? 10,
          min_encounters: params.minEncounters ?? 2,
          min_duration_minutes: params.minDurationMinutes ?? 5,
          limit: params.limit ?? 200,
        },
        { timeout: 300000 }, // adtech fan-out can be slow (paid external API)
      );
      return response.data;
    } catch (error: any) {
      console.error('❌ AdTech co-traveler analysis failed:', error);
      throw new Error(
        `AdTech co-traveler analysis failed: ${error.response?.data?.detail || error.message}`,
      );
    }
  }

  async queryAdtechAreaSearch(params: {
    center: [number, number]; // [lat, lon]
    radiusMeters: number;
    dateRange: [Date, Date];
    timeZone?: string;
    limit?: number;
  }): Promise<AdtechAreaSearchResult> {
    try {
      const response = await this.client.post(
        '/pathfinder-postgres/adtech-area-search',
        {
          center_lat: params.center[0],
          center_lon: params.center[1],
          radius_meters: params.radiusMeters,
          start_date: this.serializeQueryDate(params.dateRange[0], params.timeZone),
          end_date: this.serializeQueryDate(params.dateRange[1], params.timeZone),
          limit: params.limit ?? 5000,
        },
        { timeout: 300000 },
      );
      return response.data;
    } catch (error: any) {
      console.error('❌ AdTech area search failed:', error);
      throw new Error(
        `AdTech area search failed: ${error.response?.data?.detail || error.message}`,
      );
    }
  }

  /**
   * Query AdTech (Venntel/GeoNeutron) signals inside — or near — a polygon
   * geofence. `polygon` is the GeoJSON outer ring as [lon, lat] pairs.
   * `bufferMeters > 0` expands the ring outward ("near"); 0 means "inside".
   */
  async queryAdtechGeofenceSearch(params: {
    polygon: Array<[number, number]>; // [lon, lat] pairs
    dateRange: [Date, Date];
    bufferMeters?: number;
    timeZone?: string;
    limit?: number;
  }): Promise<AdtechAreaSearchResult> {
    try {
      const response = await this.client.post(
        '/pathfinder-postgres/adtech-geofence-search',
        {
          polygon: params.polygon,
          start_date: this.serializeQueryDate(params.dateRange[0], params.timeZone),
          end_date: this.serializeQueryDate(params.dateRange[1], params.timeZone),
          buffer_meters: params.bufferMeters ?? 0,
          limit: params.limit ?? 5000,
        },
        { timeout: 300000 },
      );
      return response.data;
    } catch (error: any) {
      console.error('❌ AdTech geofence search failed:', error);
      throw new Error(
        `AdTech geofence search failed: ${error.response?.data?.detail || error.message}`,
      );
    }
  }

  /**
   * Load the full "pattern of life" (all observations, not area-bounded) for
   * one or more discovered AdTech devices over the selected window. The result
   * feeds the device tree / map / time-slider exactly like a student track.
   */
  async getAdtechPatternOfLife(params: {
    registrationIds: string[];
    dateRange: [Date, Date];
    timeZone?: string;
    limitPerDevice?: number;
  }): Promise<AdtechPatternOfLifeResult> {
    try {
      const response = await this.client.post(
        '/pathfinder-postgres/adtech-pattern-of-life',
        {
          registration_ids: params.registrationIds,
          start_date: this.serializeQueryDate(params.dateRange[0], params.timeZone),
          end_date: this.serializeQueryDate(params.dateRange[1], params.timeZone),
          limit_per_device: params.limitPerDevice ?? 20000,
        },
        { timeout: 300000 }, // full-history fan-out can be slow (paid external API)
      );
      return response.data;
    } catch (error: any) {
      console.error('❌ AdTech pattern-of-life failed:', error);
      throw new Error(
        `AdTech pattern of life failed: ${error.response?.data?.detail || error.message}`,
      );
    }
  }

  /**
   * Look up AdTech signals for specific IP address(es). Returns the same shape
   * as pattern-of-life (devices grouped by discovered registration id) so the
   * result plugs straight into `adtechPolToDataPoints`.
   */
  async queryAdtechIpSearch(params: {
    ipAddresses: string[];
    dateRange: [Date, Date];
    timeZone?: string;
    limitPerDevice?: number;
  }): Promise<AdtechPatternOfLifeResult> {
    try {
      const response = await this.client.post(
        '/pathfinder-postgres/adtech-ip-search',
        {
          ip_addresses: params.ipAddresses,
          start_date: this.serializeQueryDate(params.dateRange[0], params.timeZone),
          end_date: this.serializeQueryDate(params.dateRange[1], params.timeZone),
          limit_per_device: params.limitPerDevice ?? 20000,
        },
        { timeout: 300000 },
      );
      return response.data;
    } catch (error: any) {
      console.error('❌ AdTech IP search failed:', error);
      throw new Error(
        `AdTech IP search failed: ${error.response?.data?.detail || error.message}`,
      );
    }
  }

  /**
   * Preview device/signal counts for a circle OR polygon before running a full
   * (paid) area pull. Provide `center`+`radiusMeters` OR `polygon` ([lon,lat] ring).
   */
  async queryAdtechDiscoverOnly(params: {
    center?: [number, number]; // [lat, lon]
    radiusMeters?: number;
    polygon?: Array<[number, number]>; // [lon, lat] ring
    dateRange: [Date, Date];
    timeZone?: string;
  }): Promise<AdtechDiscoverResult> {
    try {
      const body: Record<string, any> = {
        start_date: this.serializeQueryDate(params.dateRange[0], params.timeZone),
        end_date: this.serializeQueryDate(params.dateRange[1], params.timeZone),
      };
      if (params.polygon && params.polygon.length >= 3) {
        body.polygon = params.polygon;
      } else if (params.center && params.radiusMeters) {
        body.center_lat = params.center[0];
        body.center_lon = params.center[1];
        body.radius_meters = Math.round(params.radiusMeters);
      }
      const response = await this.client.post(
        '/pathfinder-postgres/adtech-discover-only',
        body,
        { timeout: 120000 },
      );
      return response.data;
    } catch (error: any) {
      console.error('❌ AdTech discover-only failed:', error);
      throw new Error(
        `AdTech preview failed: ${error.response?.data?.detail || error.message}`,
      );
    }
  }

  /**
   * Fetch demographic attributes + hashed emails for AdTech registration ids
   * (Venntel BS Amplify). Returns empty collections when the tenant is not
   * licensed rather than throwing.
   */
  async getAdtechDemographics(registrationIds: string[]): Promise<AdtechDemographicsResult> {
    try {
      const response = await this.client.post(
        '/pathfinder-postgres/adtech-demographics',
        { registration_ids: registrationIds },
        { timeout: 120000 },
      );
      return response.data;
    } catch (error: any) {
      console.error('❌ AdTech demographics failed:', error);
      throw new Error(
        `AdTech demographics failed: ${error.response?.data?.detail || error.message}`,
      );
    }
  }

  /**
   * Convert an AdTech pattern-of-life result into standard PathfinderDataPoints
   * so discovered devices render as tracks in the device tree and obey the time
   * slider. `studentId` = registration_id, deviceType = 'phone' so the tree's
   * `${studentId}-phone` device-key convention is satisfied.
   */
  static adtechPolToDataPoints(
    devices: Record<string, Array<Record<string, any>>>,
  ): PathfinderDataPoint[] {
    const points: PathfinderDataPoint[] = [];
    Object.entries(devices || {}).forEach(([regId, signals]) => {
      (signals || []).forEach((s, i) => {
        const lat = Number(s.latitude);
        const lon = Number(s.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
        const ts = s.timestamp ? new Date(s.timestamp) : null;
        if (!ts || isNaN(ts.getTime())) return;
        points.push({
          id: `adtech-${regId}-${i}`,
          deviceId: `${regId}-phone`,
          deviceType: 'phone',
          studentId: regId,
          timestamp: ts,
          // PathfinderDataPoint uses [lat, lon] everywhere (map, routes, dwells).
          coordinates: [lat, lon],
          accuracy: s.accuracy != null ? Number(s.accuracy) : 0,
          speed: s.speed != null ? Number(s.speed) : undefined,
          bearing: s.bearing != null ? Number(s.bearing) : undefined,
          metadata: {
            source: 'adtech',
            ip_address: s.ip_address ?? null,
            forensic_flag: s.forensic_flag ?? null,
            country: s.country ?? null,
            carrier: s.carrier ?? null,
            timezone: s.timezone ?? null,
          },
        });
      });
    });
    return points;
  }

  // ==========================================================================
  // Overwatch (GDELT media ingested by DataHog)
  // ==========================================================================

  async getOverwatchStatus(): Promise<OverwatchStatus> {
    const response = await this.client.get('/pathfinder-postgres/overwatch/status');
    return response.data;
  }

  async getOverwatchCategories(): Promise<string[]> {
    const response = await this.client.get('/pathfinder-postgres/overwatch/categories');
    return response.data?.categories || [];
  }

  async searchOverwatch(params: OverwatchSearchParams): Promise<OverwatchSearchResult> {
    const body: Record<string, any> = {
      mode: params.mode || 'keyword',
      query: params.query,
      language: params.language || 'all',
      category: params.category,
      days: params.days ?? 30,
      limit: params.limit ?? 100,
    };
    if (params.dateRange) {
      body.start_date = this.serializeQueryDate(params.dateRange[0], params.timeZone);
      body.end_date = this.serializeQueryDate(params.dateRange[1], params.timeZone);
    }
    if (params.mode === 'area') {
      body.min_lat = params.minLat;
      body.max_lat = params.maxLat;
      body.min_lon = params.minLon;
      body.max_lon = params.maxLon;
    } else if (params.mode === 'radius') {
      body.center_lat = params.centerLat;
      body.center_lon = params.centerLon;
      body.radius_km = params.radiusKm;
    }
    try {
      const response = await this.client.post(
        '/pathfinder-postgres/overwatch/search',
        body,
        { timeout: 120000 },
      );
      return response.data;
    } catch (error: any) {
      console.error('❌ Overwatch search failed:', error);
      throw new Error(
        `OSINT search failed: ${error.response?.data?.detail || error.message}`,
      );
    }
  }

  async getOverwatchArticle(docId: string): Promise<OverwatchArticle> {
    const response = await this.client.get(
      `/pathfinder-postgres/overwatch/article/${encodeURIComponent(docId)}`,
    );
    return response.data;
  }

  // ==========================================================================
  // Saved Devices (watchlist) — persist devices of interest and reload them.
  // ==========================================================================

  async getSavedDevices(courseId?: string): Promise<SavedDevice[]> {
    const response = await this.client.get('/watchlist', {
      params: { active_only: true, ...(courseId ? { course_id: courseId } : {}) },
    });
    return response.data || [];
  }

  async saveDevice(params: SaveDeviceParams): Promise<SavedDevice> {
    const response = await this.client.post('/watchlist', {
      registration_id: params.registrationId,
      name: params.name,
      notes: params.notes,
      tags: params.tags || [],
      course_id: params.courseId,
      device_kind: params.deviceKind,
      device_type: params.deviceType,
    });
    return response.data;
  }

  async updateSavedDevice(
    id: number,
    updates: { name?: string; notes?: string; tags?: string[]; active?: boolean },
  ): Promise<SavedDevice> {
    const response = await this.client.patch(`/watchlist/${id}`, updates);
    return response.data;
  }

  async deleteSavedDevice(id: number): Promise<void> {
    await this.client.delete(`/watchlist/${id}`);
  }

  // ==========================================================================
  // Saved Areas (geofences) — persist a drawn Area Search circle and reload it.
  // ==========================================================================

  /** Approximate a lat/lon circle as a closed GeoJSON polygon ring. */
  static circleToPolygon(
    center: [number, number], // [lat, lon]
    radiusMeters: number,
    segments = 48,
  ): number[][][] {
    const [lat, lon] = center;
    const earth = 6378137; // meters
    const latRad = (lat * Math.PI) / 180;
    const ring: number[][] = [];
    for (let i = 0; i <= segments; i++) {
      const theta = (i / segments) * 2 * Math.PI;
      const dx = radiusMeters * Math.cos(theta);
      const dy = radiusMeters * Math.sin(theta);
      const dLon = (dx / (earth * Math.cos(latRad))) * (180 / Math.PI);
      const dLat = (dy / earth) * (180 / Math.PI);
      ring.push([lon + dLon, lat + dLat]); // GeoJSON order [lng, lat]
    }
    return [ring];
  }

  /** Recover the original {center:[lat,lon], radius} for a saved area. Prefers
   *  the JSON metadata stashed in `description`; otherwise derives it from the
   *  polygon (centroid + mean radius). */
  static savedAreaToCircle(area: SavedArea): { center: [number, number]; radius: number } {
    try {
      const meta = area.description ? JSON.parse(area.description) : null;
      if (meta && Array.isArray(meta.center) && typeof meta.radius === 'number') {
        const center: [number, number] = [Number(meta.center[0]), Number(meta.center[1])];
        return { center, radius: Number(meta.radius) };
      }
    } catch {
      // description wasn't our JSON — fall through to polygon math.
    }
    const ring: number[][] = area.geometry?.coordinates?.[0] || [];
    if (ring.length === 0) {
      const fallback: [number, number] = [0, 0];
      return { center: fallback, radius: 100 };
    }
    let sx = 0;
    let sy = 0;
    for (const pt of ring) {
      sx += pt[0];
      sy += pt[1];
    }
    const cLon = sx / ring.length;
    const cLat = sy / ring.length;
    // Mean distance from centroid to ring vertices (meters).
    const earth = 6378137;
    const latRad = (cLat * Math.PI) / 180;
    let dsum = 0;
    for (const pt of ring) {
      const dx = ((pt[0] - cLon) * Math.PI) / 180 * earth * Math.cos(latRad);
      const dy = ((pt[1] - cLat) * Math.PI) / 180 * earth;
      dsum += Math.sqrt(dx * dx + dy * dy);
    }
    const center: [number, number] = [cLat, cLon];
    return { center, radius: Math.round(dsum / ring.length) || 100 };
  }

  async getSavedAreas(courseId?: string): Promise<SavedArea[]> {
    // Only list Pathfinder "Saved Areas" (source='area_search'); never operational
    // COP/ATAK geofences, so this panel can't be used to delete them.
    const response = await this.client.get('/geofences', {
      params: { active_only: true, source: 'area_search', ...(courseId ? { course_id: courseId } : {}) },
    });
    return response.data || [];
  }

  async saveArea(params: SaveAreaParams): Promise<SavedArea> {
    const coordinates = PathfinderApiService.circleToPolygon(
      params.center,
      params.radiusMeters,
    );
    const description = JSON.stringify({
      center: params.center,
      radius: params.radiusMeters,
      placeName: params.placeName,
      kind: 'area-search-circle',
    });
    const response = await this.client.post('/geofences', {
      name: params.name,
      description,
      geometry: { type: 'Polygon', coordinates },
      course_id: params.courseId,
      // Tag as a Pathfinder bookmark so it's isolated from operational COP fences.
      source: 'area_search',
    });
    return response.data;
  }

  async deleteSavedArea(id: number): Promise<void> {
    await this.client.delete(`/geofences/${id}`);
  }

  // ==========================================================================
  // Biometrics (watch HR / steps / energy) - backs the BiometricsPanel tab
  // ==========================================================================

  private _buildBiometricsRequest(opts: BiometricsQuery) {
    return {
      students: opts.studentIds && opts.studentIds.length > 0 ? opts.studentIds : undefined,
      device_ids: opts.deviceIds && opts.deviceIds.length > 0 ? opts.deviceIds : undefined,
      start_date: this.serializeQueryDate(opts.dateRange[0], opts.timeZone),
      end_date: this.serializeQueryDate(opts.dateRange[1], opts.timeZone),
      course_id: opts.courseId,
      sample_types: opts.sampleTypes,
      limit: opts.limit,
    };
  }

  async getBiometricSummary(opts: BiometricsQuery): Promise<BiometricsSummary> {
    try {
      const resp = await this.client.post(
        '/pathfinder/biometrics/summary',
        this._buildBiometricsRequest(opts)
      );
      return resp.data;
    } catch (err: any) {
      console.error('BiometricSummary failed:', err);
      throw new Error(`Biometric summary failed: ${err.response?.data?.detail || err.message}`);
    }
  }

  async getBiometricSamples(opts: BiometricsQuery): Promise<BiometricSample[]> {
    try {
      const resp = await this.client.post(
        '/pathfinder/biometrics/samples',
        this._buildBiometricsRequest(opts)
      );
      return resp.data;
    } catch (err: any) {
      console.error('BiometricSamples failed:', err);
      throw new Error(`Biometric samples failed: ${err.response?.data?.detail || err.message}`);
    }
  }

  async getBiometricTimeline(
    opts: BiometricsQuery & { bucketSeconds?: number; perDevice?: boolean }
  ): Promise<BiometricsTimelineResponse> {
    try {
      const resp = await this.client.post('/pathfinder/biometrics/timeline', {
        ...this._buildBiometricsRequest(opts),
        bucket_seconds: opts.bucketSeconds ?? 60,
        per_device: opts.perDevice ?? true,
      });
      return resp.data;
    } catch (err: any) {
      console.error('BiometricTimeline failed:', err);
      throw new Error(`Biometric timeline failed: ${err.response?.data?.detail || err.message}`);
    }
  }

  // =========================================================================
  // Admin GPS cleanup (PIN + administrator role required)
  //
  // Surgical, polygon-scoped soft/hard delete of GPS noise. All cleanup
  // calls send the PIN as `X-Cleanup-PIN`. The PIN is held in component
  // state on the panel and never persisted - refreshing the page makes
  // the user re-enter it. The backend hashes the PIN on every request
  // and never stores plaintext.
  // =========================================================================

  async cleanupVerifyPin(pin: string): Promise<{ ok: boolean; user: string }> {
    const resp = await this.client.post('/pathfinder/cleanup/auth/verify', { pin });
    return resp.data;
  }

  async cleanupPreview(args: {
    pin: string;
    polygon: any;
    timeRange: { start: Date; end: Date };
    entityIds?: string[];
    target?: 'tracks' | 'dwells' | 'both';
  }): Promise<any> {
    const resp = await this.client.post(
      '/pathfinder/cleanup/preview',
      {
        polygon: args.polygon,
        time_range: { start: args.timeRange.start.toISOString(), end: args.timeRange.end.toISOString() },
        entity_ids: args.entityIds && args.entityIds.length > 0 ? args.entityIds : null,
        target: args.target || 'both',
      },
      { headers: { 'X-Cleanup-PIN': args.pin } },
    );
    return resp.data;
  }

  async cleanupQuarantine(args: {
    pin: string;
    target: 'tracks' | 'dwells';
    polygon: any;
    timeRange: { start: Date; end: Date };
    entityIds?: string[];
    reason?: string;
  }): Promise<{ affected_count: number; audit_id: number | null }> {
    const resp = await this.client.post(
      '/pathfinder/cleanup/quarantine',
      {
        target: args.target,
        polygon: args.polygon,
        time_range: { start: args.timeRange.start.toISOString(), end: args.timeRange.end.toISOString() },
        entity_ids: args.entityIds && args.entityIds.length > 0 ? args.entityIds : null,
        reason: args.reason || null,
      },
      { headers: { 'X-Cleanup-PIN': args.pin } },
    );
    return resp.data;
  }

  async cleanupHardDelete(args: {
    pin: string;
    target: 'tracks' | 'dwells';
    polygon: any;
    timeRange: { start: Date; end: Date };
    entityIds?: string[];
    reason?: string;
    confirmText: string; // must be 'DELETE'
  }): Promise<{ affected_count: number; audit_id: number | null }> {
    const resp = await this.client.post(
      '/pathfinder/cleanup/delete',
      {
        target: args.target,
        polygon: args.polygon,
        time_range: { start: args.timeRange.start.toISOString(), end: args.timeRange.end.toISOString() },
        entity_ids: args.entityIds && args.entityIds.length > 0 ? args.entityIds : null,
        reason: args.reason || null,
        confirm_text: args.confirmText,
      },
      { headers: { 'X-Cleanup-PIN': args.pin } },
    );
    return resp.data;
  }

  async cleanupRestore(args: { pin: string; auditId: number }): Promise<{ restored_count: number; restored_ids: number[] }> {
    const resp = await this.client.post(
      '/pathfinder/cleanup/restore',
      { audit_id: args.auditId },
      { headers: { 'X-Cleanup-PIN': args.pin } },
    );
    return resp.data;
  }

  async cleanupAuditLog(args: { pin: string; limit?: number; offset?: number }): Promise<{ items: any[]; limit: number; offset: number }> {
    const resp = await this.client.get('/pathfinder/cleanup/audit', {
      params: { limit: args.limit || 50, offset: args.offset || 0 },
      headers: { 'X-Cleanup-PIN': args.pin },
    });
    return resp.data;
  }
}

// ==========================================================================
// Biometrics types (exported for the panel component)
// ==========================================================================

export interface BiometricsQuery {
  studentIds?: string[];
  deviceIds?: string[];
  dateRange: [Date, Date];
  timeZone?: string;
  courseId?: string;
  sampleTypes?: string[];
  limit?: number;
}

export interface BiometricSample {
  device_id: string;
  entity_id?: string | null;
  course_id?: string | null;
  sample_type: string;
  value: number;
  unit: string;
  sample_timestamp: string; // ISO
  latitude?: number | null;
  longitude?: number | null;
  battery_level?: number | null;
}

export interface BiometricTypeStats {
  count: number;
  avg?: number | null;
  min: number;
  max: number;
  unit: string;
  last?: number | null;
  last_ts?: string | null;
}

export interface BiometricsSummary {
  window_start: string;
  window_end: string;
  duration_seconds: number;
  device_count: number;
  total_samples: number;
  per_type: Record<string, BiometricTypeStats>;
  hr_zones: Record<string, number>; // fraction 0..1
  totals: {
    active_energy_kcal?: number;
    avg_cadence_spm?: number | null;
    total_step_samples?: number;
    hr_sample_count?: number;
    battery_drain_pct_per_hour?: number;
    // distance/pace derived from the lat/lon trail
    distance_m?: number;
    distance_km?: number;
    moving_seconds?: number;
    avg_speed_mps?: number;
    avg_speed_kmh?: number;
    pace_min_per_km?: number;
    // training load (Edwards' TRIMP) and cardiac drift summaries
    trimp_score?: number;
    minutes_in_zone?: Record<string, number>;
    cardiac_drift_pct?: number;
  };
  latest: Record<string, { value: number; unit: string; at: string }>;
  insights: Array<Record<string, any>>;
}

export interface BiometricTimelinePoint {
  bucket_start: string;
  sample_type: string;
  device_id?: string | null;
  entity_id?: string | null;
  avg_value?: number | null;
  min_value?: number | null;
  max_value?: number | null;
  sample_count: number;
}

export interface BiometricsTimelineResponse {
  bucket_seconds: number;
  points: BiometricTimelinePoint[];
}

export const pathfinderApi = new PathfinderApiService();
export default pathfinderApi;
